/**
 * One billing plan (§8.1, Board 11; iPad Board 15; phone Board 16).
 *
 * Desktop and iPad: a header — `$` tile, name and `{n} visits`, the
 * headline, the total, **Terms**, a chevron and ⋮ — then, open, the kind's
 * panel, then the plan's visits beside the invoices it makes. A phone draws
 * the card as a summary with its next invoice, and the plan itself as a sheet.
 */
import { useState, type ReactNode } from 'react'
import {
  BILLING_KIND_CARDS, BILLING_TAB, TERMS_WORDS, invoiceTable, nextInvoiceSaid, planHeadline,
  stageSumChip, termsLabel, usd, type BillingWordsContext, type QuoteBillingPlanView,
  type QuoteVisitView,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollars } from '../../lib/format.js'
import { Segmented, SectionLabel } from '../../components/primitives.js'
import { FieldShell } from '../../components/fields.js'
import { EditorActions, EditorHeader, EditorInput, EditorShell } from '../../builder/editor-shell.js'
import { CollapseCaret } from '../../builder/rows/caret.js'
import { INVOICE_SCOPE_LABELS, PLAN_SETTINGS_FACTS, REMOVE_PLAN } from '../../builder/copy.js'
import { MenuSelect } from '../../components/menu-select.js'
import { TermsSelect } from '../../components/terms-select.js'
import { ContextMenu } from '../../components/context-menu.js'
import { PanelSheet, SheetButton } from '../../components/panel-sheet.js'
import { visitKindGlyph } from '../../builder/fields/kind-field.js'
import { IconVisit } from '../../builder/rows/glyphs.js'
import {
  IconChevronRight, IconClose, IconMoreVertical, IconReceipt,
} from '../../frame/icons.js'
import { CountChip, MoneyTile } from './parts.js'
import { InvoiceTable } from './invoice-table.js'
import { KindPanel, type BillingLayout } from './kind-panel.js'
import { StageSumChip } from './stages.js'

/** What a drag carries. One key, so a stray drop cannot mean anything else. */
export const DRAG_TYPE = 'application/x-rowted-visit'

export interface PlanCardProps {
  plan: QuoteBillingPlanView
  ctx: BillingWordsContext
  quoteVisits: QuoteVisitView[]
  layout: BillingLayout
  canEdit: boolean
  busy: boolean
  /** Open on arrival — the first plan, and any plan with a problem. */
  initiallyOpen: boolean
  dropping: boolean
  onDragOver: (on: boolean) => void
  onAssign: (visitId: string) => void
  onUnassign: (visitId: string) => void
  onOpenVisit: (visitId: string) => void
  onPatch: (body: Record<string, unknown>) => Promise<unknown>
  onDelete: () => void
  /**
   * Held by the page (step 21 item 5.1) so Collapse all reaches every plan
   * and a new plan arrives open. Absent, the card keeps its own.
   */
  collapsed?: boolean
  onToggle?: () => void
}

/** Whether a plan has something to fix: a stage chip that is not green, or rows that miss the total. */
export function planHasProblem(plan: QuoteBillingPlanView): boolean {
  if (plan.visitIds.length === 0) return false
  const chip = stageSumChip(plan).state
  if (chip !== 'empty' && chip !== 'ok') return true
  const table = invoiceTable(plan)
  return plan.invoices.length > 0 && !plan.invoices.some((row) => row.estimate) && !table.addsUp
}

export function PlanCard(props: PlanCardProps) {
  const {
    plan, ctx, quoteVisits, layout, canEdit, busy, initiallyOpen, dropping, onDragOver, onAssign,
    onPatch,
  } = props
  const [ownOpen, setOwnOpen] = useState(initiallyOpen)
  const open = props.collapsed === undefined ? ownOpen : !props.collapsed
  const toggle = props.onToggle ?? (() => setOwnOpen((was) => !was))
  /** The name's popup — rename and remove, the engine's popover (step 21 item 5.3). */
  const [settingsAt, setSettingsAt] = useState<HTMLElement | null>(null)
  /** Base only · With options, under Invoices this will create (step 21 item 5.4). */
  const [scope, setScope] = useState<'base' | 'all'>('base')
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const phone = layout === 'phone'

  const drop = {
    onDragOver: (event: React.DragEvent) => {
      if (!canEdit || !event.dataTransfer.types.includes(DRAG_TYPE)) return
      event.preventDefault()
      onDragOver(true)
    },
    onDragLeave: () => onDragOver(false),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault()
      const visitId = event.dataTransfer.getData(DRAG_TYPE)
      if (visitId) onAssign(visitId)
    },
  }

  const name = canEdit
    ? (
      <button type="button" data-plan-name="" aria-haspopup="dialog" aria-expanded={settingsAt !== null}
        onClick={(event) => setSettingsAt(event.currentTarget)}
        className="truncate text-left text-[15px] font-bold text-text hover:text-accent">
        {plan.name}
      </button>
    )
    : <b className="truncate text-[15px] font-bold text-text">{plan.name}</b>

  const menuItems = [
    { key: 'rename', label: BILLING_TAB.rename, onSelect: () => {
      setMenu(null)
      setSettingsAt(document.querySelector<HTMLElement>(`[data-plan-card="${plan.id}"] [data-plan-name]`))
    } },
    { key: 'delete', label: BILLING_TAB.delete, tone: 'danger' as const,
      onSelect: () => { setMenu(null); props.onDelete() } },
  ]

  /* The plan as Done-with-every-option would bill it, where that differs (step 21 item 5.4). */
  const shown = scope === 'all' && plan.allIn
    ? { ...plan, total: plan.allIn.total, invoices: plan.allIn.invoices }
    : plan
  const invoices = (
    <div className="flex flex-col gap-2">
      {plan.allIn && (
        <span className="self-start">
          <Segmented
            label="Invoices scope"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'base', label: INVOICE_SCOPE_LABELS.base },
              { value: 'all', label: INVOICE_SCOPE_LABELS.all },
            ]}
          />
        </span>
      )}
      <InvoiceTable plan={shown} ctx={ctx} empty={emptyWords(plan)} />
    </div>
  )

  const settings = settingsAt && (
    <PlanSettings plan={plan} anchor={settingsAt} onClose={() => setSettingsAt(null)}
      onRename={(next) => void onPatch({ name: next })} onRemove={props.onDelete} />
  )

  if (phone) return <PhonePlan {...props} name={name} />

  return (
    <section data-plan-card={plan.id} {...drop}
      className="overflow-hidden rounded-[14px] border border-line bg-surface">
      {/*
        * The visit card's header (step 21 item 5.2): px-3.5 py-3, the caret
        * first and the tile a little in from it, so a plan folds from the
        * same place a visit, a package and a choice box do.
        */}
      <header className="flex items-center gap-3 px-3.5 py-3">
        <CollapseCaret collapsed={!open} onToggle={toggle} label={plan.name} />
        <span className="ml-0.5 flex shrink-0"><MoneyTile size={40} /></span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-2">
            {name}
            <CountChip>{BILLING_TAB.visitsChip(plan.visitIds.length)}</CountChip>
          </span>
          <span className="truncate text-[12.5px] text-text-muted" data-plan-headline="">
            {planHeadline(plan, ctx)}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <b className="tnum text-[15px] font-bold text-text">{usd(plan.total)}</b>
          <span className="flex flex-col items-start gap-0.5">
            <SectionLabel>{TERMS_WORDS.label}</SectionLabel>
            {canEdit
              ? (
                <TermsSelect termsMode={plan.termsMode} termsDays={plan.termsDays}
                  customerDays={plan.customerTermsDays} disabled={busy}
                  onChange={(next) => void onPatch(next)} />
              )
              : <span className="text-[13.5px] text-text">{termsLabel(plan.effectiveTermsDays)}</span>}
          </span>
          {canEdit && (
            <button type="button" aria-label={`${plan.name} options`}
              onClick={(event) => {
                const box = event.currentTarget.getBoundingClientRect()
                setMenu({ x: box.right - 208, y: box.bottom + 4 })
              }}
              className="inline-flex p-1 text-text-faint hover:text-text">
              <IconMoreVertical size={16} />
            </button>
          )}
        </span>
      </header>
      {menu && <ContextMenu items={menuItems} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />}
      {settings}

      {open && canEdit && (
        <KindPanel plan={plan} ctx={ctx} visits={quoteVisits} layout={layout} busy={busy} onPatch={onPatch} />
      )}

      {open && (
        <div className={cn('grid border-t border-line-soft',
          layout === 'portrait' ? 'grid-cols-1' : 'grid-cols-2')}>
          <div className={cn('px-[18px] pb-4 pt-3.5', dropping && 'bg-accent-soft',
            layout === 'portrait' ? 'border-b border-line-soft' : 'border-r border-line-soft')}>
            <VisitsOnPlan {...props} />
          </div>
          <div className="px-[18px] pb-4 pt-3.5">
            {invoices}
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * The plan's own popup (step 21 item 5.3): the engine's popover, as a visit's
 * and a choice's are — its name, and Remove along the foot. Renaming used to
 * be a ⋮ menu item that turned the header into a text box.
 */
function PlanSettings({ plan, anchor, onClose, onRename, onRemove }: {
  plan: QuoteBillingPlanView
  anchor: HTMLElement
  onClose: () => void
  onRename: (name: string) => void
  onRemove: () => void
}) {
  const [name, setName] = useState(plan.name)
  const done = () => {
    if (name.trim() && name.trim() !== plan.name) onRename(name.trim())
    onClose()
  }
  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={plan.name}
      header={<EditorHeader kind="invoice" name={plan.name}
        facts={PLAN_SETTINGS_FACTS(BILLING_KIND_CARDS[plan.kind].title, plan.visitIds.length)} />}
      footer={(
        <EditorActions
          onRemove={() => { onRemove(); onClose() }}
          removeLabel={REMOVE_PLAN}
          onDone={done}
          disabled={!name.trim()}
        />
      )}
    >
      <div data-plan-settings="" className="flex flex-col gap-3.5">
        <FieldShell label="Name" hint="What the plan is called here and on the proposal.">
          <EditorInput
            autoFocus
            aria-label="Plan name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter' && name.trim()) done() }}
          />
        </FieldShell>
      </div>
    </EditorShell>
  )
}

/** Why there are no invoices, rather than that there are none. */
function emptyWords(plan: QuoteBillingPlanView): string {
  if (plan.visitIds.length === 0) return 'Nothing yet — this plan has no visits on it.'
  if (plan.kind === 'milestones') return 'Add a stage and its invoice appears here.'
  if (plan.kind === 'period') return 'Give the visits dates, or change the months, and the periods appear here.'
  return 'Nothing yet.'
}

function VisitsOnPlan({
  plan, quoteVisits, canEdit, busy, onAssign, onUnassign, onOpenVisit,
}: PlanCardProps) {
  const visits = plan.visitIds
    .map((id) => quoteVisits.find((visit) => visit.id === id))
    .filter((visit): visit is QuoteVisitView => visit !== undefined)
  const others = quoteVisits.filter((visit) => visit.billingPlanId !== plan.id)
  const unplanned = quoteVisits.filter((visit) => visit.billingPlanId === null).length
  return (
    <div data-plan-visits="">
      <SectionLabel className="block">{BILLING_TAB.visitsOnPlan}</SectionLabel>
      <div className="mt-2 flex flex-col gap-1.5">
        {visits.map((visit) => (
          <VisitChip key={visit.id} visit={visit} canEdit={canEdit} busy={busy}
            onOpen={() => onOpenVisit(visit.id)} onRemove={() => onUnassign(visit.id)} />
        ))}
        {canEdit && others.length > 0 && (
          <span className="relative block">
            <MenuSelect size="field" label={`Add a visit to ${plan.name}`} placeholder={BILLING_TAB.addVisit}
              value={null} disabled={busy}
              onChange={(visitId) => { if (visitId) onAssign(visitId) }}
              /*
               * The choice box's **+ Add visits** rows (Aaron, step 23 item
               * 1.1): the visit's own tile, its name, what it is and where it
               * bills now, and its price at the right — rather than a bare
               * name beside a plan name.
               */
              options={others.map((one) => ({
                value: one.id,
                label: one.name || 'Untitled visit',
                help: one.billingPlanName
                  ? `${one.phrase.card} · on ${one.billingPlanName}`
                  : one.phrase.card,
                detail: formatDollars(one.total),
                glyph: one.role ? visitKindGlyph(one.role, 15) : <IconVisit size={15} />,
                tileTone: 'accent' as const,
                group: one.billingPlanId ? 'On another plan' : 'Needs a plan',
              }))} />
            {unplanned > 0 && (
              <span className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 text-[12px]
                               font-semibold text-warn">
                {BILLING_TAB.needsPlan(unplanned)}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  )
}

/** A visit on the plan (Board 11): kind tile, name and Optional, its card phrase, total, ×. */
function VisitChip({
  visit, canEdit, busy, onOpen, onRemove,
}: { visit: QuoteVisitView; canEdit: boolean; busy: boolean; onOpen: () => void; onRemove: () => void }) {
  return (
    <div draggable={canEdit} data-plan-visit={visit.id}
      onDragStart={(event) => {
        event.dataTransfer.setData(DRAG_TYPE, visit.id)
        event.dataTransfer.effectAllowed = 'move'
      }}
      className={cn('flex items-center gap-[9px] rounded-[10px] border border-line bg-surface px-[9px] py-[7px]',
        canEdit && 'cursor-grab active:cursor-grabbing')}>
      <span aria-hidden className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px]
                                   bg-accent-soft text-accent">
        {visitKindGlyph(visit.role, 13)}
      </span>
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 flex-col text-left">
        <span className="flex min-w-0 items-center gap-1.5">
          <b className="truncate text-[12.5px] font-semibold text-text hover:text-accent">{visit.name}</b>
          {visit.optional && (
            <span className="inline-flex h-[18px] shrink-0 items-center rounded-[5px] bg-info-bg px-1.5
                             text-[10.5px] font-semibold text-info">Optional</span>
          )}
        </span>
        <span className="truncate text-[11.5px] text-text-muted">{visit.phrase.card}</span>
      </button>
      <b className="tnum shrink-0 text-[12.5px] font-bold text-text">{usd(visit.total)}</b>
      {canEdit && (
        <button type="button" aria-label={`Take ${visit.name} off this plan`} disabled={busy} onClick={onRemove}
          className="inline-flex shrink-0 text-text-faint hover:text-danger">
          <IconClose size={13} />
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The phone (Board 16)
// ---------------------------------------------------------------------------

function PhonePlan(props: PlanCardProps & { name: ReactNode }) {
  const { plan, ctx, quoteVisits, canEdit, busy, onPatch } = props
  const [open, setOpen] = useState(false)
  const next = nextInvoiceSaid(plan, ctx)
  return (
    <>
      <button type="button" data-plan-card={plan.id} onClick={() => setOpen(true)}
        className="flex w-full flex-col overflow-hidden rounded-[14px] border border-line bg-surface text-left">
        <span className="flex items-start gap-3 px-3.5 py-3">
          <MoneyTile size={38} />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-center gap-2">
              <b className="truncate text-[15.5px] font-bold text-text">{plan.name}</b>
              <CountChip>{BILLING_TAB.visitsChip(plan.visitIds.length)}</CountChip>
            </span>
            <span className="line-clamp-3 text-[13.5px] leading-[1.4] text-text-muted" data-plan-headline="">
              {planHeadline(plan, ctx)}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-1.5">
            <b className="tnum text-[17px] font-bold text-text">{formatDollars(plan.total)}</b>
            <span className="inline-flex h-5 items-center rounded-[6px] bg-line-soft px-1.5 text-[11px]
                             font-semibold text-text-muted">{termsLabel(plan.effectiveTermsDays)}</span>
          </span>
        </span>
        {next && (
          <span className="flex items-center gap-2 border-t border-line-soft px-3.5 py-2.5 text-[13px] text-text-muted">
            <IconReceipt size={14} className="shrink-0 text-text-faint" />
            <span className="min-w-0 flex-1 truncate">{next}</span>
            <IconChevronRight size={15} className="shrink-0 text-text-faint" />
          </span>
        )}
      </button>

      <PanelSheet open={open} onClose={() => setOpen(false)} tall title={plan.name}
        sub={`${BILLING_KIND_CARDS[plan.kind].title} · ${usd(plan.total)}`}
        footer={(
          <div className="flex w-full flex-col gap-2">
            {plan.kind === 'milestones' && <span><StageSumChip plan={plan} /></span>}
            <div className="flex w-full">
              <SheetButton onClick={() => setOpen(false)}>{BILLING_TAB.done}</SheetButton>
            </div>
          </div>
        )}>
        <div className="flex flex-col gap-4 px-4 pb-4 pt-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-[12.5px] text-text-muted">{TERMS_WORDS.label}</span>
            {canEdit
              ? (
                <TermsSelect full termsMode={plan.termsMode} termsDays={plan.termsDays}
                  customerDays={plan.customerTermsDays} disabled={busy}
                  onChange={(change) => void onPatch(change)} />
              )
              : <span className="text-[14px] text-text">{termsLabel(plan.effectiveTermsDays)}</span>}
            <span className="text-[11.5px] text-text-faint">{BILLING_TAB.dueEvery(plan.effectiveTermsDays)}</span>
          </div>
          {canEdit && (
            <KindPanel plan={plan} ctx={ctx} visits={quoteVisits} layout="phone" busy={busy} onPatch={onPatch} />
          )}
          <VisitsOnPlan {...props} />
          <InvoiceTable plan={plan} ctx={ctx} empty={emptyWords(plan)} />
        </div>
      </PanelSheet>
    </>
  )
}
